import React, { useState, useEffect } from "react";
import Preloader from "./Components/Pre";
import Navbar from "./Components/Navbar";
import Home from "./Screens/Home/Home";
import Login from "./Screens/Login";
import Signup from "./Screens/Signup";
import Register from "./Screens/Register";
import Onboarding from "./Screens/Onboarding";
import ResetPassword from "./Screens/ResetPassword";
import AdminAddUser from "./Screens/Admin/AddUser";
import Activity from "./Screens/Activity";
import ActivityDetail from "./Screens/Activity/ActivityDetail";
import Achiever from "./Screens/Achiever";
import Members from "./Screens/Members";
import MemberDetail from "./Screens/Members/MemberDetail";
import Footer from "./Components/Footer";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from "react-router-dom";
import ScrollToTop from "./Components/ScrollToTop";
import "./style.css";
import "./App.css";
import ComingSoon from "./Screens/SiteState/ComingSoon";
import './Utils/i18n';
import AchievementDetail from "./Screens/Achiever/AchievementDetail";
import { END_POINT_CONFIG } from "./Config";
import axios from "axios";
import ResumeBuilder from './Screens/ResumeBuilder';
import { AuthProvider, useAuth } from "./Context/AuthContext";

function ProtectedRoute({ children, adminOnly = false }) {
  const { isLoggedIn, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes({ configData }) {
  const { isLoggedIn, isFirstTime, user } = useAuth();

  if (configData.STATE !== 'launch') return <ComingSoon />;

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/members" element={<Members />} />
      <Route path="/members/:id" element={<MemberDetail />} />
      <Route path="/activity" element={<Activity />} />
      <Route path="/activity/:id" element={<ActivityDetail />} />
      <Route path="/achiever" element={<Achiever />} />
      <Route path="/achiever/:id" element={<AchievementDetail />} />

      {/* Auth routes — redirect logged-in users away */}
      <Route path="/login" element={isLoggedIn ? <Navigate to="/members" replace /> : <Login />} />
      <Route path="/signup" element={isLoggedIn ? <Navigate to="/members" replace /> : <Signup />} />

      {/* Protected auth-flow routes */}
      <Route path="/reset-password" element={
        <ProtectedRoute><ResetPassword /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><Onboarding /></ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin/add-user" element={
        <ProtectedRoute adminOnly><AdminAddUser /></ProtectedRoute>
      } />

      {/* Legacy register route */}
      <Route path="/register" element={<Register />} />
      <Route path="/register/:profileUrl" element={<Register />} />

      <Route path="/resume-builder" element={<ResumeBuilder />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const [load, upadateLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [configData, setConfig] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      upadateLoad(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  async function makeApiCall() {
    setLoading(true);
    try {
      const response = await axios.get(END_POINT_CONFIG);
      setConfig(response.data.data);
    } catch (e) {
      setConfig({ STATE: 'launch' });
    }
    setLoading(false);
  }

  useEffect(() => {
    makeApiCall();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Preloader load={load || loading} />
        <div className="App" id={load ? "no-scroll" : "scroll"}>
          <Navbar isLaunched={configData.STATE === 'launch'} />
          <ScrollToTop />
          <AppRoutes configData={configData} />
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
